import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/axios';
import { clsx } from 'clsx';
import { roleNavigations, flattenNavItems } from '../config/navigation';

import { NotificationBell } from './NotificationBell';
import { ConditionalAd } from './ConditionalAd';
import { useCurrency } from '../context/CurrencyContext';
import {
    Menu,
    X,
    LogOut,
    PawPrint,
    Ambulance
} from 'lucide-react';
import { GlobalReviewTrigger } from './GlobalReviewTrigger';
import { getApiUrl } from '../lib/utils';
import { MarqueeBanner } from './MarqueeBanner';
import { useTranslation } from 'react-i18next';


const SHOW_BUSINESS_HEADER_ROLES = ['GROOMING_AGENCY', 'DAYCARE', 'TRAINER', 'PARTNER', 'ECOMMERCE_AGENT', 'PET_SHOP', 'CONSULTATION', 'OWNER', 'BREEDER', 'VET_PHARMACY'];

const getInitials = (name: string) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

interface LayoutProps {
    children: React.ReactNode;

}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const normalizedPath = location.pathname.toLowerCase().replace(/\/$/, '');
    const dashboardRoles = ['GROOMING_AGENCY', 'DAYCARE', 'TRAINER', 'ECOMMERCE_AGENT', 'PET_SHOP', 'CONSULTATION', 'PARTNER', 'INSURANCE', 'BREEDER', 'VET_PHARMACY'];
    const dashboardPaths = [
        '/groomer', '/grooming-agency', '/grooming-agency/overview',
        '/daycare-mgmt', '/daycare-mgmt/overview',
        '/trainer-mgmt', '/trainer-mgmt/overview',
        '/agent', '/agent/dashboard',
        '/partner', '/insurance', '/breeder'
    ];
    const isModuleDashboard =
        (dashboardRoles.includes(user?.role || '') && dashboardPaths.includes(normalizedPath)) ||
        (['DAYCARE', 'GROOMING_AGENCY', 'TRAINER', 'PARTNER', 'OWNER', 'ECOMMERCE_AGENT', 'PET_SHOP', 'CONSULTATION', 'INSURANCE', 'BREEDER', 'VET_PHARMACY'].includes(user?.role || ''));
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
    const [showMoreMenu, setShowMoreMenu] = React.useState(false);
    // Derived state for plan details
    const tierName = user?.tier || 'FREE';
    const isPro = tierName.toUpperCase() === 'PRO';

    const [profilePhotoUrl, setProfilePhotoUrl] = React.useState<string | null>('');
    const [displayName, setDisplayName] = React.useState<string>('');
    const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
    const { currency } = useCurrency();
    const fetchProfile = React.useCallback(async () => {
        try {
            let endpoint = '';
            if (user?.role === 'OWNER') endpoint = '/owners/me';
            else if (user?.role === 'GROOMING_AGENCY') endpoint = '/grooming-agency/me';
            else if (user?.role === 'DAYCARE') endpoint = '/daycare/profile';
            else if (user?.role === 'DOCTOR') endpoint = '/doctor/me';
            else if (user?.role === 'TRAINER') endpoint = '/trainer/profile';
            else if (user?.role === 'ADMIN') endpoint = '/admin/me';
            else if (user?.role === 'NGO') endpoint = '/social-impact/admin/ngos';
            else if (user?.role === 'CSR') endpoint = '/auth/me';
            else if (user?.role === 'PARTNER') endpoint = '/partners/profile';
            else if (user?.role === 'BREEDER') endpoint = '/breeder/profile';
            else if (user?.role === 'ECOMMERCE_AGENT' || user?.role === 'PET_SHOP' || user?.role === 'CONSULTATION' || user?.role === 'VET_PHARMACY') endpoint = '/ecommerce/me';

            if (endpoint) {
                const { data } = await api.get(endpoint);
                const profileData = endpoint === '/auth/me' ? data.user : data;

                let name = profileData.businessName || profileData.daycareName || profileData.agencyName ||
                    profileData.clinicName || profileData.name || profileData.userName ||
                    user?.username || '';

                let photo = profileData.profilePhotoBlob || profileData.logoBlob ||
                    profileData.profilePhotoUrl || profileData.profilePicture ||
                    profileData.logoUrl || profileData.logo || profileData.profilePhoto ||
                    profileData.photoUrl || '';

                if (endpoint === '/auth/me' && profileData) {
                    const moduleData = profileData.trainer || profileData.daycareManager ||
                        profileData.groomingAgency || profileData.doctor ||
                        profileData.eCommerceAgent;
                    if (moduleData) {
                        name = moduleData.businessName || moduleData.daycareName ||
                            moduleData.agencyName || moduleData.clinicName ||
                            moduleData.name || name;

                        photo = moduleData.profilePhotoBlob || moduleData.logoBlob ||
                            moduleData.profilePhotoUrl || moduleData.logoUrl ||
                            moduleData.profilePicture || photo;
                    }
                }

                setDisplayName(name);
                setProfilePhotoUrl(photo || null);
            }
        } catch (error) {
            console.error('Failed to fetch profile for layout', error);
            if (user?.username) {
                setDisplayName(user.username);
            }
        }
    }, [user]);


    // Auto-collapse sidebar on smaller screens (but above mobile)
    React.useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1024 && window.innerWidth >= 768) {
                setIsSidebarCollapsed(true);
            } else if (window.innerWidth >= 1024) {
                setIsSidebarCollapsed(false);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Explicit profile update listener
    React.useEffect(() => {
        const handleProfileUpdate = () => {
            console.log('Profile update event received in Layout');
            fetchProfile();
        };
        window.addEventListener('profile-updated', handleProfileUpdate);
        return () => window.removeEventListener('profile-updated', handleProfileUpdate);
    }, [fetchProfile]);

    React.useEffect(() => {
        if (user) {
            const timeoutId = setTimeout(() => fetchProfile(), 0);
            return () => clearTimeout(timeoutId);
        }
    }, [user, fetchProfile]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };



    const transformAmbulanceItems = (items: any[]) => {
        const isAmbulance = user?.role === 'DAYCARE' && (user as any)?.registrationTypes?.includes('Ambulance Services');
        if (!isAmbulance) return items;

        return items.map((item: any) => {
            if ('items' in item && Array.isArray(item.items)) {
                return {
                    ...item,
                    items: item.items.map((sub: any) => {
                        if (sub.to === '/daycare-mgmt/activities' || sub.moduleId === 'activities') {
                            return {
                                ...sub,
                                to: '/daycare-mgmt/vehicles',
                                label: 'Vehicle Management',
                                name: 'Vehicle Management',
                                icon: Ambulance,
                                moduleId: 'vehicles',
                                description: 'Ambulance vehicle fleet & driver management'
                            };
                        }
                        return sub;
                    })
                };
            }

            if (item.to === '/daycare-mgmt/activities' || item.moduleId === 'activities') {
                return {
                    ...item,
                    to: '/daycare-mgmt/vehicles',
                    label: 'Vehicle Management',
                    name: 'Vehicle Management',
                    icon: Ambulance,
                    moduleId: 'vehicles',
                    description: 'Ambulance vehicle fleet & driver management'
                };
            }
            return item;
        });
    };

    const getNavItems = () => {
        if (!user?.role) return [];

        // Use central navigation registry
        const rawItems = roleNavigations[user.role] || roleNavigations['OWNER'];
        const items = transformAmbulanceItems(rawItems);
        const flatItems = flattenNavItems(items);

        const mappedItems = flatItems.map((item: { to?: string; href?: string; label?: string; name?: string, icon: React.ComponentType<{ size?: number | string; className?: string }>, moduleId: string }) => ({
            ...item,
            to: item.to || item.href || '',
            label: item.label || item.name || '',
            icon: item.icon,
            moduleId: item.moduleId
        }));

        let filteredItems = mappedItems;
        if (user?.permissions && Array.isArray(user.permissions)) {
            filteredItems = mappedItems.filter((item: { moduleId: string }) => {
                const perm = user.permissions.find((p: { moduleId: string; level: string }) => p.moduleId === item.moduleId);
                return !perm || perm.level !== 'NONE';
            });
        }

        return filteredItems as { to: string; label: string; icon: React.ComponentType<{ size?: number | string; className?: string }>; isPro?: boolean; hasPro?: boolean }[];
    };

    const getGroupedNavItems = () => {
        if (!user?.role) return [];
        const rawItems = roleNavigations[user.role] || roleNavigations['OWNER'];
        const items = transformAmbulanceItems(rawItems);
        
        return items.map((groupOrItem: any) => {
            if ('items' in groupOrItem) {
                const filtered = groupOrItem.items.filter((item: any) => {
                    if (!user?.permissions || !Array.isArray(user.permissions)) return true;
                    const perm = user.permissions.find((p: any) => p.moduleId === item.moduleId);
                    return !perm || perm.level !== 'NONE';
                }).map((item: any) => ({
                    ...item,
                    to: item.to || item.href || '',
                    label: item.label || item.name || '',
                    icon: item.icon,
                    moduleId: item.moduleId
                }));
                return { ...groupOrItem, items: filtered };
            } else {
                let isVisible = true;
                if (user?.permissions && Array.isArray(user.permissions)) {
                    const perm = user.permissions.find((p: any) => p.moduleId === groupOrItem.moduleId);
                    if (perm && perm.level === 'NONE') isVisible = false;
                }
                if (!isVisible) return null;
                return {
                    ...groupOrItem,
                    to: groupOrItem.to || groupOrItem.href || '',
                    label: groupOrItem.label || groupOrItem.name || '',
                    icon: groupOrItem.icon,
                    moduleId: groupOrItem.moduleId
                };
            }
        }).filter(Boolean);
    };

    const navItems = getNavItems();
    const groupedNavItems = getGroupedNavItems();

    return (
        <div className="flex flex-col h-screen overflow-hidden">
            {user?.role === 'OWNER' && <MarqueeBanner />}
            <div
                className="flex-1 flex flex-col md:flex-row font-sans transition-colors duration-300 overflow-hidden"
                style={{
                    backgroundColor: 'var(--theme-bg)',
                    backgroundImage: 'var(--theme-bg-image)',
                    backgroundSize: 'cover',
                    backgroundAttachment: 'fixed',
                    color: 'var(--theme-text)'
                }}
            >
                {/* Mobile Header */}
                <div
                    className="md:hidden flex justify-between items-center px-5 py-4 sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-100 shadow-sm"
                >
                    <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                            <span className="font-black text-xs uppercase tracking-widest text-brand-600 leading-none">{t('ui_pet_smart_7dd96d', `Pet Smart`)}</span>
                            <span className="font-bold text-[10px] text-slate-400 uppercase tracking-tighter mt-0.5">{t('ui_monitor_d2986a', `Monitor`)}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <NotificationBell />
                        {profilePhotoUrl ? (
                            <div className="w-8 h-8 rounded-full overflow-hidden border border-slate-200 bg-slate-50">
                                <img
                                    src={getApiUrl(profilePhotoUrl)}
                                    className="w-full h-full object-cover"
                                    alt={t('ui_me_318b27', `Me`)}
                                />
                            </div>
                        ) : (
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-black select-none border border-slate-200/50 bg-[#6366f1] shadow-sm shrink-0"
                            >
                                {getInitials(displayName || user?.username || 'U')}
                            </div>
                        )}
                    </div>

                </div>

                {/* Sidebar (Desktop) */}
                <aside
                    className={clsx(
                        "fixed inset-y-0 left-0 z-[100] transform transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] flex flex-col shadow-2xl",
                        "md:static md:z-40 md:h-[calc(100%-2rem)] md:ml-4 md:my-4 md:mr-0 md:rounded-[32px] md:overflow-hidden md:translate-x-0 md:shadow-[10px_0_30px_rgba(0,0,0,0.03)]",
                        isSidebarCollapsed ? "w-24 px-2" : "w-[272px] px-4",
                        !isMobileMenuOpen ? "-translate-x-full" : "translate-x-0"
                    )}
                    style={{
                        backgroundColor: 'var(--theme-sidebar-bg)',
                        backgroundImage: 'var(--theme-sidebar-bg-image)',
                        backgroundSize: 'cover',
                        borderColor: 'var(--theme-border)'
                    }}
                >
                    <div className={clsx("p-6 flex items-center border-b-0 gap-3", isSidebarCollapsed ? "justify-center flex-col px-0" : "justify-start")}>
                        {SHOW_BUSINESS_HEADER_ROLES.includes(user?.role || '') ? (
                            <div
                                className="flex items-center gap-4 cursor-pointer group transition-all duration-300"
                                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            >
                                {isSidebarCollapsed ? (
                                    <div className="w-12 h-12 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm group-hover:scale-110 active:scale-95 transition-transform">
                                        <Menu className="w-6 h-6" />
                                    </div>
                                ) : (
                                    displayName ? (
                                        <div className="flex flex-col min-w-0 w-full">
                                            <div className="flex items-center gap-3">
                                                {profilePhotoUrl ? (
                                                    <div className="w-10 h-10 rounded-xl overflow-hidden shadow-sm border border-slate-100 flex-shrink-0 bg-slate-50 flex items-center justify-center group-hover:scale-105 transition-transform">
                                                        <img
                                                            src={getApiUrl(profilePhotoUrl)}
                                                            className="w-full h-full object-cover"
                                                            alt={t('ui_logo_8c2857', `Logo`)}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-black flex-shrink-0 shadow-sm border border-slate-100 bg-[#6366f1] group-hover:scale-105 transition-transform select-none"
                                                    >
                                                        {getInitials(displayName)}
                                                    </div>
                                                )}
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-sm font-black tracking-tight truncate uppercase leading-tight" style={{ color: 'var(--theme-sidebar-text)' }} title={displayName}>
                                                        {displayName.length > 13 ? displayName.substring(0, 13) + '...' : displayName}
                                                    </span>
                                                    {user?.role === 'DAYCARE' && (user as any)?.registrationTypes?.length > 0 && (
                                                        <span className="text-[8px] font-bold text-slate-500 uppercase tracking-widest mt-0.5 truncate max-w-[150px]" title={(user as any).registrationTypes.join(', ')}>
                                                            {(user as any).registrationTypes.join(', ')}
                                                        </span>
                                                    )}
                                                    <span className="text-[7.2px] font-black uppercase tracking-tighter leading-none mt-1.5 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-fuchsia-500 whitespace-nowrap">{t('sidebar.poweredBy', 'Powered by Pet Smart Monitor')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="text-xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-fuchsia-500 truncate">{t('ui_pet_smart_monit_cea0c7', `Pet Smart Monitor`)}</span>
                                    )
                                )}
                            </div>
                        ) : (
                            <>
                                <button
                                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                                    className={clsx(
                                        "hidden md:flex text-slate-400 hover:text-brand-600 transition-all shrink-0 p-1.5 hover:bg-slate-100/50 rounded-lg",
                                        isSidebarCollapsed && "hover:bg-slate-100"
                                    )}
                                    title={isSidebarCollapsed ? "Expand menu" : "Collapse menu"}
                                >
                                    <Menu className="w-5 h-5" />
                                </button>
                                {!isSidebarCollapsed ? (
                                    <span className="text-xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-fuchsia-500 truncate">{t('ui_pet_smart_monit_cea0c7', `Pet Smart Monitor`)}</span>
                                ) : (
                                    <PawPrint className="w-8 h-8 text-cyan-400 shrink-0" />
                                )}
                            </>
                        )}
                    </div>

                    <div className="p-1.5 flex-1 overflow-y-auto scrollbar-hide">
                        <div className="mb-4">
                            <nav className="space-y-1 mt-1">
                                {groupedNavItems.map((groupOrItem: any, idx: number) => {
                                    const renderNavItem = (item: any) => {
                                        const isActive = location.pathname === item.to || (location.pathname + location.search) === item.to;
                                        const showProBadge = !isPro && (item.isPro || item.hasPro);

                                        return (
                                            <Link
                                                key={item.to}
                                                to={item.to}
                                                title={isSidebarCollapsed ? item.label : undefined}
                                                className={clsx(
                                                    "flex items-center rounded-2xl transition-all duration-200 group font-bold relative text-[14px]",
                                                    isSidebarCollapsed ? "justify-center p-2.5 mx-1" : "px-4 py-2 mx-2",
                                                    isActive
                                                        ? "shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
                                                        : "hover:bg-slate-50/50"
                                                )}
                                                style={{
                                                    background: isActive ? 'var(--theme-primary)' : 'transparent',
                                                    color: isActive ? '#ffffff' : 'var(--theme-sidebar-text)'
                                                }}
                                                onClick={() => setIsMobileMenuOpen(false)}
                                            >
                                                <item.icon
                                                    className={clsx(
                                                        "w-5 h-5 transition-colors shrink-0",
                                                        !isSidebarCollapsed && "mr-3.5",
                                                        isActive ? "text-white" : "text-slate-400 group-hover:text-slate-600"
                                                    )}
                                                />
                                                {!isSidebarCollapsed && (
                                                    <span className="truncate">{t(`nav.item.${item.label.replace(/\s+/g, '')}`, item.label) as string}</span>
                                                )}
                                                {showProBadge && !isSidebarCollapsed && (
                                                    <span className="ml-auto px-2 py-0.5 rounded-full text-[9px] font-black bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm">
                                                        {t('ui_pro_9e3360', `PRO`)}</span>
                                                )}
                                                {isActive && isSidebarCollapsed && (
                                                    <div className="absolute left-0 w-1 h-6 bg-white rounded-r-full" />
                                                )}
                                            </Link>
                                        );
                                    };

                                    if ('items' in groupOrItem) {
                                        if (groupOrItem.items.length === 0) return null;
                                        return (
                                            <div key={`group-${idx}`} className={clsx("mb-4", isSidebarCollapsed && "mt-4")}>
                                                {!isSidebarCollapsed && (
                                                    <h3 className="px-6 mb-2 mt-4 text-[9px] font-black text-slate-400/80 uppercase tracking-widest leading-loose">
                                                        {groupOrItem.name}
                                                    </h3>
                                                )}
                                                <div className="space-y-1">
                                                    {groupOrItem.items.map((item: any) => renderNavItem(item))}
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        return renderNavItem(groupOrItem);
                                    }
                                })}
                            </nav>
                        </div>

                        {!isPro && !isSidebarCollapsed && (
                            <div className="mt-4 transition-all duration-300">
                                <ConditionalAd position="SIDEBAR" className="mx-2" />
                            </div>
                        )}
                    </div>

                    <div className={clsx("pt-4 mt-auto border-t border-slate-100/60", user?.role === 'OWNER' ? "pb-12" : "pb-4", isSidebarCollapsed ? "px-2" : "px-3 mx-3")}>
                        <button
                            onClick={handleLogout}
                            className={clsx(
                                "flex items-center w-full transition-all duration-200 group font-bold rounded-2xl",
                                isSidebarCollapsed ? "justify-center p-2.5" : "px-4 py-2 hover:bg-red-50 text-slate-500 hover:text-red-600"
                            )}
                            title={isSidebarCollapsed ? "Logout" : undefined}
                        >
                            <LogOut className={clsx("w-5 h-5 transition-colors shrink-0", !isSidebarCollapsed && "mr-3.5", "text-slate-400 group-hover:text-red-600")} />
                            {!isSidebarCollapsed && <span>{t('sidebar.logout', 'Logout')}</span>}
                        </button>
                    </div>
                </aside>

                {/* Modern "More" Grid Menu (Full Screen Overlay) */}
                {showMoreMenu && (
                    <div className="fixed inset-0 z-[110] animate-in fade-in duration-300">
                        <div
                            className="absolute inset-0 bg-slate-900/80 backdrop-blur-md"
                            onClick={() => setShowMoreMenu(false)}
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-[2.5rem] p-6 pt-10 shadow-[0_-20px_50px_rgba(0,0,0,0.2)] animate-in slide-in-from-bottom-full duration-500 ease-out max-h-[85vh] overflow-y-auto">
                            <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-8 cursor-pointer" onClick={() => setShowMoreMenu(false)} />

                            <div className="flex justify-between items-center mb-8 px-2">
                                <div>
                                    <h2 className="text-2xl font-black text-slate-900 leading-tight">{t('sidebar.exploreMore', 'Explore More')}</h2>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-1">{t('ui_pet_smart_monit_cea0c7', `Pet Smart Monitor`)}</p>
                                </div>
                                <button
                                    onClick={() => setShowMoreMenu(false)}
                                    className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 active:scale-90 transition-all"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="grid grid-cols-3 gap-y-8 gap-x-4 mb-10">
                                {navItems.filter((item: { to: string }) => !['/dashboard', '/pets', '/pet-care', '/shop'].includes(item.to)).map((item: { to: string; label: string; icon: React.ComponentType<{ size?: number | string; className?: string }> }) => (
                                    <Link
                                        key={item.to}
                                        to={item.to}
                                        onClick={() => setShowMoreMenu(false)}
                                        className="flex flex-col items-center gap-3 group"
                                    >
                                        <div className="w-16 h-16 rounded-3xl bg-slate-50 flex items-center justify-center text-slate-500 group-hover:bg-brand-50 group-hover:text-brand-600 transition-all shadow-sm border border-slate-100">
                                            <item.icon className="w-6 h-6" />
                                        </div>
                                        <span className="text-[10px] font-black text-slate-900 uppercase tracking-tight text-center leading-tight">
                                            {t(`nav.item.${item.label.replace(/\s+/g, '')}`, item.label)}
                                        </span>
                                    </Link>
                                ))}
                            </div>

                            <div className="border-t border-slate-100 pt-8 pb-4">
                                <button
                                    onClick={() => {
                                        setShowMoreMenu(false);
                                        handleLogout();
                                    }}
                                    className="flex items-center justify-center gap-3 w-full py-4 rounded-2xl bg-red-50 text-red-600 font-black text-xs uppercase tracking-widest shadow-sm active:scale-[0.98] transition-all"
                                >
                                    <LogOut size={16} /> {t('sidebar.logout', 'Logout Account')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <main className="flex-1 min-w-0 overflow-y-auto relative scroll-smooth h-full">
                    <header
                        className={clsx(
                            "z-40 flex justify-between items-center transition-all duration-300 sticky top-0 w-full left-0 py-6 px-4 md:px-8 bg-[var(--theme-bg)]/80 backdrop-blur-xl border-b border-transparent",
                            isMobileMenuOpen && "opacity-0 md:opacity-100 pointer-events-none"
                        )}
                    >
                        {(!isModuleDashboard || user?.role === 'ADMIN') && (
                            <div className="flex-1">
                                {user?.role !== 'OWNER' && (
                                    <h2 className="text-lg md:text-xl font-black truncate" style={{ color: 'var(--theme-text)' }}>
                                        {(() => {
                                            const matchedItem = [...navItems].sort((a, b) => b.to.length - a.to.length).find(item => location.pathname.startsWith(item.to) && item.to !== '/dashboard');
                                            if (matchedItem) return t(`nav.item.${matchedItem.label.replace(/\s+/g, '')}`, matchedItem.label);
                                            if (location.pathname === '/dashboard') return t('nav.item.Dashboard', 'Dashboard');
                                            if (location.pathname.includes('/new')) return t('nav.item.AddNew', 'Add New');
                                            if (location.pathname.includes('/edit')) return t('nav.item.Edit', 'Edit');
                                            return t('nav.item.Overview', 'Overview');
                                        })()}
                                    </h2>
                                )}
                            </div>
                        )}
                        <div className={clsx(
                            "flex items-center space-x-2 md:space-x-4 ml-auto transition-all duration-300",
                            "absolute top-6 right-4 md:right-8 z-[70]"
                        )}>
                            {user?.role === 'OWNER' && (
                                <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 rounded-full border shadow-sm" style={{ borderColor: 'var(--theme-primary)', backgroundColor: 'var(--theme-card)' }}>
                                    <span className="text-[8px] md:text-[10px] font-black uppercase" style={{ color: 'var(--theme-text-muted)' }}>{t('ui_region_f447ac', `Region`)}</span>
                                    <span className="text-[10px] md:text-xs font-bold" style={{ color: 'var(--theme-text)' }}>{currency}</span>
                                    <div className="w-[1px] h-2.5 md:h-3 mx-0.5 md:mx-1" style={{ backgroundColor: 'var(--theme-primary)', opacity: 0.3 }} />
                                    <span className="text-[8px] md:text-[10px] font-black uppercase" style={{ color: 'var(--theme-primary)' }}>{tierName}</span>
                                </div>
                            )}
                            <div className="hidden md:block">
                                <NotificationBell />
                            </div>
                            <div className="md:hidden">
                                {/* Mobile Profile or other small elements can go here */}
                            </div>
                        </div>
                    </header>
                    <div className={clsx(
                        "w-full transition-all duration-300",
                        isModuleDashboard ? "max-w-none px-2 md:pl-4 md:pr-4 pt-4 pb-24 md:pb-10" : "mx-auto max-w-[1600px] p-4 md:p-8 pb-24 md:pb-8"
                    )}>
                        <div className="w-full text-left">
                            {children}
                        </div>
                    </div>
                </main>

                {/* Mobile Bottom Navigation Bar */}
                <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-white/90 border-t border-slate-100 flex items-center justify-around px-2 py-3 shadow-[0_-10px_30px_rgba(0,0,0,0.05)]">
                    {navItems.slice(0, 4).map((item: { to: string; label: string; icon: React.ComponentType<{ size?: number | string; className?: string }> }) => {
                        const isActive = location.pathname === item.to;
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={clsx(
                                    "flex flex-col items-center gap-1 flex-1 transition-all duration-300 transform",
                                    isActive ? "scale-110" : "opacity-50"
                                )}
                            >
                                <div className={clsx(
                                    "p-2 rounded-xl transition-all",
                                    isActive ? "bg-brand-600 text-white shadow-lg shadow-brand-200" : "text-slate-600"
                                )}>
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <span className={clsx("text-[9px] font-black uppercase tracking-widest text-center truncate w-full px-1", isActive ? "text-brand-600" : "text-slate-400")}>
                                    {t(`nav.item.${item.label.replace(/\s+/g, '')}`, item.label).split(' ')[0]}
                                </span>
                            </Link>
                        )
                    })}
                    <button
                        onClick={() => setShowMoreMenu(true)}
                        className={clsx(
                            "flex flex-col items-center gap-1 flex-1 transition-all",
                            showMoreMenu ? "opacity-100 scale-110" : "opacity-50"
                        )}
                    >
                        <div className="p-2 rounded-xl text-slate-600">
                            <Menu className="w-5 h-5" />
                        </div>
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                            {t('sidebar.more', 'More')}
                        </span>
                    </button>
                </div>

                {/* Overlay for mobile sidebar */}
                {isMobileMenuOpen && (
                    <div
                        className="fixed inset-0 bg-slate-950/40 backdrop-blur-xl z-[90] md:hidden animate-in fade-in duration-300"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                )}

                <GlobalReviewTrigger />

            </div>
        </div>
    );
};
